/**
 * datevBelegeApi — FE-Client für die DATEV-Belege-Paket (ZIP) HTTP-Function (M7b-3).
 *
 * `buildDatevBelegePaket` (`functions/src/datev/buildDatevBelegePaket.ts`) ist eine
 * Firebase **HTTP-Function** (`onRequest`, NICHT `onCall`) — die ZIP kann > 10 MB
 * werden, das Callable-Response-Limit ist unbrauchbar (architecture-m7b.md §
 * „Architektur-Seam"). Deshalb wird sie hier NICHT über `httpsCallable`
 * (`firebase/functions`, Muster `tenantFileApi.ts`) aufgerufen, sondern über
 * `fetch` gegen ihre HTTP-URL mit manuellem Bearer-Token-Header — Muster
 * `invoiceApi.ts` (`FUNCTIONS_URL`/`ENHANCE_TEXT_FUNCTION_URL`) und
 * `RegisterPage.tsx` (`logLegalAgreementsWithIp`-Direktaufruf). Der Bearer-Token
 * kommt aus `getAuthToken()` (`authUtils.ts`, Muster `apiRequest.ts`).
 *
 * Response ist ein gestreamtes `application/zip` — kein Base64 (anders als
 * `tenantFileApi.ts`s `getTenantDocumentFile`-Callable), daher direkter
 * `response.blob()` + `<a download>`-Trigger (kein `base64ToUint8Array`-Umweg nötig).
 *
 * SIGNATUR-ABWEICHUNG von architecture-m7b.md (dokumentiert, s. changelog.md):
 * die Architektur-Pseudo-Signatur ist `Promise<void>`. Die Function liefert aber
 * best-effort einen `X-Datev-Belege-Skipped`-Header (übersprungene Belege, z. B.
 * fehlende Datei) — diese Information darf nicht verworfen werden (sichtbares
 * Feedback statt stillem Datenverlust, Golden Rule „Silent Fallbacks"). Der
 * Rückgabewert wurde daher pragmatisch zu {@link DownloadDatevBelegePaketResult}
 * erweitert.
 *
 * @module datevBelegeApi
 */

import { getAuthToken } from './authUtils';
import type { DatevDocumentRef, DatevHeaderConfig } from '../utils/datevExport';
import logger from '../utils/logger';

/**
 * Basis-URL der Firebase-HTTP-Functions (europe-west3). Muster `invoiceApi.ts`
 * (`FUNCTIONS_URL`) — `REACT_APP_FUNCTIONS_URL`, sonst der produktive Projekt-Host
 * (`billution-invoices`, s. CLAUDE.md „Firebase project ID").
 */
const FUNCTIONS_BASE_URL =
  process.env.REACT_APP_FUNCTIONS_URL || 'https://europe-west3-billution-invoices.cloudfunctions.net';

/** Vollständige URL der `buildDatevBelegePaket`-HTTP-Function. */
const DATEV_BELEGE_PAKET_URL = `${FUNCTIONS_BASE_URL}/buildDatevBelegePaket`;

/**
 * Request-Body der `buildDatevBelegePaket`-Function — exakt das Shape aus
 * `DatevBelegePaketRequestBody` (`functions/src/datev/buildDatevBelegePaket.ts`).
 * `refs` trägt NIE einen Storage-Pfad (Sicherheits-Kernregel, architecture-m7b.md):
 * die Function lädt Pfade autoritativ aus dem jeweiligen Firestore-Doc.
 *
 * M7b-5c Option A: `csvLines`/`documentXml` ENTFERNT — die Function baut die `document.xml`
 * server-autoritativ und emittiert KEINE CSV mehr. Der EXTF-Buchungsstapel ist ein reines
 * FE-Artefakt (getrennter CP1252-Download); `header` liefert Berater-/Mandantennummer für
 * den document.xml-Ordnungsbegriff.
 */
export interface DatevBelegePaketRequest {
  fromISO: string;
  toISO: string;
  header: DatevHeaderConfig;
  refs: Array<Pick<DatevDocumentRef, 'collection' | 'docId' | 'fileKind' | 'guid' | 'filenameInZip'>>;
}

/**
 * Ergebnis eines erfolgreichen Belege-Paket-Downloads: Anzahl der von der Function
 * best-effort übersprungenen Belege (`X-Datev-Belege-Skipped`-Header), der rohe
 * Header-Wert für eine detailliertere Anzeige (`"3; access_denied=1, …"`), sowie die
 * konkreten übersprungenen `documentKey`s (`X-Datev-Belege-Skipped-Keys`) — der Aufrufer
 * leert damit die lokale CSV-Beleglink-Spalte für real fehlende Dateien (nie ein toter
 * DATEV-Link). `skipped === 0` / `skippedKeys === []`, wenn die Header fehlen.
 */
export interface DownloadDatevBelegePaketResult {
  skipped: number;
  skippedDetail: string | null;
  /** Übersprungene `documentKey`s (`${collection}:${docId}`), ggf. gekappt (s. Function-Cap). */
  skippedKeys: string[];
}

/** Parst den `X-Datev-Belege-Skipped`-Header (`"0"` oder `"<n>; reason=k, …"`) → Zähler. */
function parseSkippedCount(raw: string | null): { skipped: number; skippedDetail: string | null } {
  if (!raw) {
    return { skipped: 0, skippedDetail: null };
  }
  const countPart = raw.split(';')[0]?.trim() ?? '0';
  const count = Number.parseInt(countPart, 10);
  return { skipped: Number.isFinite(count) ? count : 0, skippedDetail: raw };
}

/** Parst den `X-Datev-Belege-Skipped-Keys`-Header (komma-getrennte `documentKey`-Liste). */
function parseSkippedKeys(raw: string | null): string[] {
  if (!raw) {
    return [];
  }
  return raw
    .split(',')
    .map((key) => key.trim())
    .filter((key) => key.length > 0);
}

/** Extrahiert den Dateinamen aus `Content-Disposition`, sonst `fallback`. */
function extractFilename(contentDisposition: string | null, fallback: string): string {
  if (!contentDisposition) return fallback;
  const match = /filename="?([^";]+)"?/i.exec(contentDisposition);
  return match?.[1] ?? fallback;
}

/**
 * Triggert einen Browser-Download eines ZIP-Blobs via Object-URL `<a download>`
 * (Muster `tenantFileApi.ts::triggerTenantFileDownload`).
 */
function triggerZipDownload(blob: Blob, fileName: string): void {
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
}

/**
 * Ruft die `buildDatevBelegePaket`-HTTP-Function auf, lädt die gestreamte ZIP als
 * Blob und triggert den Browser-Download. Fehler (Auth/Netzwerk/Server) werden als
 * `Error` mit einer möglichst sprechenden Nachricht geworfen — der Aufrufer
 * (`DatevExportPage`) mappt sie auf eine UI-Fehlermeldung, OHNE den unabhängigen
 * CSV-Export (M7) zu beeinträchtigen.
 *
 * @param req Belege-Paket-Request (Header + Refs — KEINE CSV/XML; s. M7b-5c Option A).
 * @returns Anzahl/Detail + `documentKey`s der server-seitig übersprungenen Belege (best-effort).
 * @throws `Error` bei fehlendem Auth-Token, Netzwerkfehler oder Non-2xx-Response.
 */
export async function downloadDatevBelegePaket(
  req: DatevBelegePaketRequest,
): Promise<DownloadDatevBelegePaketResult> {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Belege-Paket: kein Auth-Token verfügbar. Bitte erneut anmelden.');
  }

  let response: Response;
  try {
    response = await fetch(DATEV_BELEGE_PAKET_URL, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(req),
    });
  } catch (networkErr) {
    logger.error('datevBelegeApi: network error', networkErr);
    throw new Error('Belege-Paket konnte nicht geladen werden (Netzwerkfehler).');
  }

  if (!response.ok) {
    let message = `Belege-Paket fehlgeschlagen (HTTP ${response.status}).`;
    try {
      const errorBody = (await response.clone().json()) as { error?: string } | null;
      if (errorBody && typeof errorBody.error === 'string' && errorBody.error.length > 0) {
        message = errorBody.error;
      }
    } catch {
      // Response-Body war kein JSON — generische Meldung bleibt bestehen.
    }
    logger.error('datevBelegeApi: request failed', { status: response.status, message });
    throw new Error(message);
  }

  const { skipped, skippedDetail } = parseSkippedCount(
    response.headers.get('X-Datev-Belege-Skipped'),
  );
  const skippedKeys = parseSkippedKeys(response.headers.get('X-Datev-Belege-Skipped-Keys'));
  const fallbackName = `Belege_${req.fromISO.replace(/-/g, '')}_${req.toISO.replace(/-/g, '')}.zip`;
  const fileName = extractFilename(response.headers.get('Content-Disposition'), fallbackName);

  const blob = await response.blob();
  triggerZipDownload(blob, fileName);

  return { skipped, skippedDetail, skippedKeys };
}
